import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorMessage, User } from '../api';
import { useAuth } from '../auth/AuthContext';
import { safeNext } from '../auth/RequireAuth';
import { Alert, Loading } from '../Components/ui';

/** Step 1: ask for an invitation email. */
function RequestInvite({ next }: { next: string }) {
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await api.post('/auth/invite', { email, next });
            setSent(true);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <Alert type="success">
                Check your inbox! We sent an invitation link to <strong>{email}</strong>. Open it to finish creating your account.
            </Alert>
        );
    }

    return (
        <>
            {error && <Alert type="error">{error}</Alert>}
            <form className="fi-form" onSubmit={submit}>
                <label>
                    Email
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" placeholder="your.email@example.com" />
                    <span className="hint">We will email you an invitation link to verify your address.</span>
                </label>
                <button type="submit" className="btn-submit" disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</button>
            </form>
        </>
    );
}

/** Step 2: the invitation link was opened; set name and password. */
function CompleteRegistration({ token, next }: { token: string; next: string }) {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState<string | null>(null);
    const [invalid, setInvalid] = useState<string | null>(null);
    const [form, setForm] = useState({ name: '', phone: '', password: '', confirm: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        api.get<{ email: string }>(`/auth/invite/${encodeURIComponent(token)}`)
            .then(res => setEmail(res.data.email))
            .catch(err => setInvalid(errorMessage(err)));
    }, [token]);

    const update = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [key]: e.target.value }));

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (form.password !== form.confirm) return setError('Passwords do not match');
        setBusy(true);
        setError(null);
        try {
            const res = await api.post<{ user: User; next: string | null }>('/auth/register', {
                token, name: form.name, password: form.password, phone: form.phone || undefined,
            });
            setUser(res.data.user);
            navigate(safeNext(res.data.next, next), { replace: true });
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    if (invalid) {
        return (
            <>
                <Alert type="error">{invalid}</Alert>
                <Link className="btn-outline" to="/register">Request a new invitation</Link>
            </>
        );
    }
    if (!email) return <Loading />;

    return (
        <>
            {error && <Alert type="error">{error}</Alert>}
            <form className="fi-form" onSubmit={submit}>
                <label>
                    Email
                    <input type="email" value={email} disabled />
                </label>
                <label>
                    Full name
                    <input type="text" value={form.name} onChange={update('name')} required autoComplete="name" placeholder="Your full name" />
                </label>
                <label>
                    Mobile number (optional)
                    <input type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" placeholder="070 123 45 67" />
                    <span className="hint">Used to prefill Swish payments.</span>
                </label>
                <label>
                    Password
                    <input type="password" value={form.password} onChange={update('password')} required minLength={10} autoComplete="new-password" />
                    <span className="hint">At least 10 characters.</span>
                </label>
                <label>
                    Confirm password
                    <input type="password" value={form.confirm} onChange={update('confirm')} required minLength={10} autoComplete="new-password" />
                </label>
                <button type="submit" className="btn-submit" disabled={busy}>{busy ? 'Creating account…' : 'Create account'}</button>
            </form>
        </>
    );
}

export default function Register() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const token = params.get('token');
    const next = safeNext(params.get('next'));

    if (user && !token) return <Navigate to={next} replace />;

    return (
        <section className="section page-section narrow">
            <span className="section-label">Sign up</span>
            <h1 className="page-title">{token ? 'Complete your account' : 'Create an account'}</h1>
            <p className="section-desc">
                {next.startsWith('/checkout')
                    ? 'You need an account to purchase hosting. It only takes a minute.'
                    : 'Get access to hosting, domains and your control panel.'}
            </p>

            <div className="form-card" data-aos="fade-up">
                {token ? <CompleteRegistration token={token} next={next} /> : <RequestInvite next={next} />}
                <div className="form-footer">
                    Already have an account? <Link className="text-link" to={`/login?next=${encodeURIComponent(next)}`}>Log in</Link>
                </div>
            </div>
        </section>
    );
}
